import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  alpha,
  Box,
  ButtonBase,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import {
  ArrowBack,
  AttachFileOutlined,
  CheckCircle,
  CreateOutlined,
  DescriptionOutlined,
  GppGoodOutlined,
  InfoOutlined,
  OpenInNew,
  PlaceOutlined
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import PdfViewer from 'components/PdfViewer'
import {
  BookingAgreementEmittableActionType,
  BookingAgreementReceipt,
  LeaseContractsBookingAgreementResponse,
  useLeaseContractsBookingAgreement
} from 'services/contracts/hooks/useLeaseContractsBookingAgreement'
import { useLeaseContract } from 'services/contracts/hooks/useLeaseContracts'
import { useLeaseContractFlats } from 'services/contracts/hooks/useLeaseContractsFlats'
import { useLeaseContractsPaymentSchedulesList } from 'services/contracts/hooks/useLeaseContractsPaymentSchedulesList'
import { useLeaseContractsDepositAccountBalance } from 'services/contracts/hooks/useLeaseContractsDepositAccountBalance'
import { useDeviceMeta } from 'services/device'
import { formatMoney, getLeaseStatusMeta } from './leases.utils'
import theme from 'styles/theme'

interface AdditionalServicesBookingAgreementConsentProps {
  contractId: number
  metadata: LeaseContractsBookingAgreementResponse
  onAccepted: (receipt: BookingAgreementReceipt) => void
  onAlreadyAccepted: () => void
}

const EVENT_RETRY_BACKOFF_MS = [500, 1500]
const CARD_SHADOW = '0px 1px 3px rgba(0, 0, 0, 0.06)'

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const CardShell: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      backgroundColor: '#fff',
      boxShadow: CARD_SHADOW,
      display: 'flex',
      flexDirection: 'column',
      width: '100%'
    }}>
    {children}
  </Box>
)

const CardHeader: FC<{ icon: ReactNode; children: ReactNode }> = ({ icon, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      px: '24px',
      pt: '16px',
      pb: '13px',
      borderBottom: `1px solid ${theme.r4r.greyLight}`,
      width: '100%'
    }}>
    <Box sx={{ color: theme.r4r.navy, display: 'flex' }}>{icon}</Box>
    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: theme.r4r.navy }}>
      {children}
    </Typography>
  </Box>
)

const DataRow: FC<{ label: string; value: string; last?: boolean }> = ({ label, value, last }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
      py: '10px',
      borderBottom: last ? 'none' : `1px solid ${theme.r4r.greyLight}`
    }}>
    <Typography sx={{ fontSize: '13px', color: theme.r4r.grey }}>{label}</Typography>
    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: theme.r4r.ink, textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
)

// Booking agreement signing ("Panel Klienta 3.0": Figma "Umowy — Podpisz umowę").
const AdditionalServicesBookingAgreementConsent: FC<
  AdditionalServicesBookingAgreementConsentProps
> = ({ contractId, metadata, onAccepted, onAlreadyAccepted }) => {
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const { deviceMeta, isDeviceMetaReady } = useDeviceMeta()
  const { startSession, logEvent, acceptAgreement } = useLeaseContractsBookingAgreement(contractId)
  const { leaseContract } = useLeaseContract(contractId)
  const { leaseContractsFlats } = useLeaseContractFlats(contractId)
  const { leaseContractsPaymentSchedulesList } = useLeaseContractsPaymentSchedulesList(contractId)
  const { leaseContractsDepositAccountBalance } = useLeaseContractsDepositAccountBalance(contractId)

  const [sessionId, setSessionId] = useState<string | null>(null)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [isStartingSession, setIsStartingSession] = useState(false)
  const [scrolledToEnd, setScrolledToEnd] = useState(false)
  const [checkboxTicked, setCheckboxTicked] = useState(false)
  const [emittedScrollEnd, setEmittedScrollEnd] = useState(false)
  const [emittedCheckboxTick, setEmittedCheckboxTick] = useState(false)
  const [signatureReady, setSignatureReady] = useState(false)

  const startSessionRunRef = useRef(false)

  const consentLabel = useMemo(
    () =>
      metadata.consentStatementText ||
      'Zapoznałem/am się z treścią umowy rezerwacyjnej oraz załącznikami.',
    [metadata.consentStatementText]
  )

  const acceptButtonLabel = useMemo(
    () =>
      metadata.acceptanceButtonLabel ||
      formatMessage({
        id: 'additionalServicesBookingAgreement.signCta',
        defaultMessage: 'Podpisz umowę'
      }),
    [formatMessage, metadata.acceptanceButtonLabel]
  )

  const pdfSha256Short = useMemo(() => {
    const hash = metadata.bookingAgreementPdfSha256
    return hash ? hash.slice(0, 8) : null
  }, [metadata.bookingAgreementPdfSha256])

  const attachments = metadata.attachments ?? []
  const currency = leaseContract?.paymentCurrency ?? ''
  const dates =
    leaseContract
      ? `${dayjs(leaseContract.leaseStart).format('DD.MM.YYYY')} — ${dayjs(
          leaseContract.effectiveLeaseEnd || leaseContract.leaseEnd
        ).format('DD.MM.YYYY')}`
      : ''
  const address = leaseContractsFlats[0]?.flatIdNumber
  const clientName = [leaseContract?.owner?.firstName, leaseContract?.owner?.lastName]
    .filter(Boolean)
    .join(' ')
  const rent = leaseContractsPaymentSchedulesList[0]?.finalGrossAmount
  const periodMonths = leaseContract
    ? Math.max(
        1,
        Math.round(
          dayjs(leaseContract.effectiveLeaseEnd || leaseContract.leaseEnd).diff(
            dayjs(leaseContract.leaseStart),
            'month',
            true
          )
        )
      )
    : null
  const depositValue = leaseContractsDepositAccountBalance?.depositBalanceValue
  const statusMeta = leaseContract ? getLeaseStatusMeta(leaseContract.leaseStatus) : null

  useEffect(() => {
    if (startSessionRunRef.current) return
    if (!isDeviceMetaReady) return
    startSessionRunRef.current = true

    const run = async () => {
      setIsStartingSession(true)
      try {
        const result = await startSession.mutateAsync(deviceMeta)
        setSessionId(result.sessionId)
        setSessionError(null)
      } catch (error: any) {
        const status = error?.response?.status
        if (status === 409) {
          setSessionError(
            formatMessage({
              id: 'additionalServicesBookingAgreement.notReady',
              defaultMessage: 'Umowa jeszcze nie jest gotowa, wróć później.'
            })
          )
        } else if (status === 403) {
          setSessionError(
            formatMessage({
              id: 'additionalServicesBookingAgreement.wisHidden',
              defaultMessage: 'Umowa rezerwacyjna w tej formie nie wymaga akceptacji w aplikacji.'
            })
          )
        } else {
          setSessionError(
            formatMessage({
              id: 'additionalServicesBookingAgreement.errorNetwork',
              defaultMessage: 'Brak połączenia z internetem. Sprawdź sieć i spróbuj ponownie.'
            })
          )
        }
      } finally {
        setIsStartingSession(false)
      }
    }

    run()
  }, [deviceMeta, formatMessage, isDeviceMetaReady, startSession])

  const emitEventWithRetry = useCallback(
    async (actionType: BookingAgreementEmittableActionType) => {
      if (!sessionId) return false
      let lastError: any
      for (let attempt = 0; attempt <= EVENT_RETRY_BACKOFF_MS.length; attempt += 1) {
        try {
          await logEvent.mutateAsync({ sessionId, actionType, deviceMeta })
          return true
        } catch (error) {
          lastError = error
          const delay = EVENT_RETRY_BACKOFF_MS[attempt]
          if (delay !== undefined) {
            await sleep(delay)
          }
        }
      }
      // eslint-disable-next-line no-console
      console.warn('[BookingAgreement] failed to emit event', actionType, lastError)
      return false
    },
    [deviceMeta, logEvent, sessionId]
  )

  const handleScrolledToEnd = useCallback(async () => {
    if (scrolledToEnd) return
    setScrolledToEnd(true)
    if (emittedScrollEnd) return
    setEmittedScrollEnd(true)
    const ok = await emitEventWithRetry('CONTRACT_SCROLLED_END')
    if (!ok) {
      setEmittedScrollEnd(false)
      enqueueSnackbar(
        formatMessage({
          id: 'additionalServicesBookingAgreement.errorNetwork',
          defaultMessage: 'Brak połączenia z internetem. Sprawdź sieć i spróbuj ponownie.'
        }),
        { variant: 'error' }
      )
    }
  }, [emitEventWithRetry, emittedScrollEnd, enqueueSnackbar, formatMessage, scrolledToEnd])

  const handleCheckboxChange = async (_: unknown, checked: boolean) => {
    setCheckboxTicked(checked)
    if (!checked) return
    if (emittedCheckboxTick) return
    setEmittedCheckboxTick(true)
    const ok = await emitEventWithRetry('CONTRACT_CHECKBOX_TICKED')
    if (!ok) {
      setEmittedCheckboxTick(false)
      setCheckboxTicked(false)
      enqueueSnackbar(
        formatMessage({
          id: 'additionalServicesBookingAgreement.errorNetwork',
          defaultMessage: 'Brak połączenia z internetem. Sprawdź sieć i spróbuj ponownie.'
        }),
        { variant: 'error' }
      )
    }
  }

  const handleAccept = async () => {
    if (!sessionId) return
    try {
      const receipt = await acceptAgreement.mutateAsync({
        sessionId,
        consentStatement: consentLabel,
        deviceMeta
      })
      onAccepted(receipt)
    } catch (error: any) {
      const status = error?.response?.status
      const serverDetail: string | undefined = error?.response?.data?.detail
      if (status === 409) {
        enqueueSnackbar(
          serverDetail ||
            formatMessage({
              id: 'additionalServicesBookingAgreement.errorAlreadyAccepted',
              defaultMessage: 'Umowa została już zaakceptowana.'
            }),
          { variant: 'info' }
        )
        onAlreadyAccepted()
        return
      }
      if (status === 400) {
        setCheckboxTicked(false)
        setEmittedCheckboxTick(false)
        enqueueSnackbar(
          serverDetail ||
            formatMessage({
              id: 'additionalServicesBookingAgreement.errorJourneyIncomplete',
              defaultMessage: 'Zaznacz proszę ponownie zgodę, aby kontynuować.'
            }),
          { variant: 'warning' }
        )
        return
      }
      enqueueSnackbar(
        serverDetail ||
          formatMessage({
            id: 'additionalServicesBookingAgreement.errorGeneric',
            defaultMessage: 'Coś poszło nie tak. Spróbuj ponownie.'
          }),
        { variant: 'error' }
      )
    }
  }

  const backTo = '/additional-services'
  const acceptDisabled =
    !sessionId ||
    !scrolledToEnd ||
    !checkboxTicked ||
    !signatureReady ||
    acceptAgreement.isLoading

  const pageTitle = formatMessage({
    id: 'additionalServicesBookingAgreement.signTitle',
    defaultMessage: 'Podpisz umowę'
  })

  const header = isDesktop ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '20px' }}>
      <IconButton
        onClick={() => navigate(backTo)}
        sx={{ width: '36px', height: '36px', color: theme.r4r.navy, borderRadius: 0 }}>
        <ArrowBack sx={{ fontSize: '22px' }} />
      </IconButton>
      <Typography
        sx={{ fontSize: '24px', fontWeight: 700, lineHeight: '28.8px', color: theme.r4r.navy }}>
        {pageTitle}
      </Typography>
    </Box>
  ) : null

  const hero = (
    <Box
      sx={{
        background: theme.r4r.gradient,
        padding: isDesktop ? '20px 24px' : '20px',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '16px'
      }}>
      <Box>
        <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#fff' }}>
          {leaseContract?.contractNumber || '—'}
        </Typography>
        {!!dates && (
          <Typography sx={{ fontSize: '12px', color: '#fff', opacity: 0.6, mt: '4px' }}>
            {dates}
          </Typography>
        )}
        {!!address && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mt: '6px', opacity: 0.7 }}>
            <PlaceOutlined sx={{ fontSize: '16px', color: '#fff' }} />
            <Typography sx={{ fontSize: '13px', color: '#fff' }}>{address}</Typography>
          </Box>
        )}
      </Box>
      {!!statusMeta && (
        <Box sx={{ backgroundColor: alpha(theme.r4r.yellow, 0.35), padding: '4px 14px', flexShrink: 0 }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
            {formatMessage(statusMeta.i18n)}
          </Typography>
        </Box>
      )}
    </Box>
  )

  const documentCard = (
    <CardShell>
      <CardHeader icon={<DescriptionOutlined sx={{ fontSize: '16px' }} />}>
        {formatMessage({
          id: 'additionalServicesBookingAgreement.documentPreview',
          defaultMessage: 'Podgląd dokumentu'
        })}
      </CardHeader>
      <Box sx={{ p: '16px' }}>
        {metadata.bookingAgreementPdf ? (
          <Box
            sx={{
              border: `1px solid ${theme.r4r.greyLight}`,
              height: isDesktop ? '320px' : '50vh',
              minHeight: '240px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
            <PdfViewer
              fileUrl={metadata.bookingAgreementPdf}
              height='100%'
              onScrolledToEnd={handleScrolledToEnd}
            />
          </Box>
        ) : (
          <Box
            sx={{
              border: `1px solid ${theme.r4r.greyLight}`,
              height: '240px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px'
            }}>
            <DescriptionOutlined sx={{ fontSize: '48px', color: theme.r4r.grey }} />
            <Typography sx={{ fontSize: '13px', color: theme.r4r.grey }}>
              {formatMessage({
                id: 'additionalServicesBookingAgreement.noDocument',
                defaultMessage:
                  'Obecnie umowa nie jest dostępna, skontaktuj się ze swoim opiekunem'
              })}
            </Typography>
          </Box>
        )}
        {metadata.bookingAgreementPdf && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: '12px' }}>
            <Link
              href={metadata.bookingAgreementPdf}
              target='_blank'
              rel='noopener noreferrer'
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '13px',
                fontWeight: 600,
                color: theme.r4r.azure,
                textDecoration: 'none'
              }}>
              <OpenInNew sx={{ fontSize: '16px' }} />
              {formatMessage({
                id: 'additionalServicesBookingAgreement.openFullView',
                defaultMessage: 'Otwórz w pełnym widoku'
              })}
            </Link>
          </Box>
        )}
        {pdfSha256Short && (
          <Typography sx={{ fontSize: '11px', color: theme.r4r.grey, mt: '8px' }}>
            {formatMessage(
              {
                id: 'additionalServicesBookingAgreement.documentVersion',
                defaultMessage: 'Wersja dokumentu: {hash}'
              },
              { hash: pdfSha256Short }
            )}
          </Typography>
        )}
        {!scrolledToEnd && !!metadata.bookingAgreementPdf && (
          <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '8px' }}>
            {formatMessage({
              id: 'additionalServicesBookingAgreement.consentScrollHint',
              defaultMessage: 'Przewiń dokument do końca, aby móc zaznaczyć zgodę.'
            })}
          </Typography>
        )}
      </Box>
    </CardShell>
  )

  const detailsCard = (
    <CardShell>
      <CardHeader icon={<InfoOutlined sx={{ fontSize: '16px' }} />}>
        {formatMessage({
          id: 'additionalServicesBookingAgreement.contractData',
          defaultMessage: 'Dane umowy'
        })}
      </CardHeader>
      <Box sx={{ px: '24px', py: '16px' }}>
        <DataRow
          label={formatMessage({
            id: 'additionalServicesBookingAgreement.client',
            defaultMessage: 'Klient'
          })}
          value={clientName || '—'}
        />
        {!!rent && (
          <DataRow
            label={formatMessage({
              id: 'additionalServicesBookingAgreement.fee',
              defaultMessage: 'Opłata'
            })}
            value={`${formatMoney(rent, currency)} / ${formatMessage({
              id: 'additionalServicesBookingAgreement.perMonth',
              defaultMessage: 'mies.'
            })}`}
          />
        )}
        {periodMonths !== null && (
          <DataRow
            label={formatMessage({
              id: 'additionalServicesBookingAgreement.period',
              defaultMessage: 'Okres'
            })}
            value={formatMessage(
              {
                id: 'additionalServicesBookingAgreement.periodMonths',
                defaultMessage: '{count} miesięcy'
              },
              { count: periodMonths }
            )}
          />
        )}
        <DataRow
          last
          label={formatMessage({
            id: 'additionalServicesBookingAgreement.depositAmount',
            defaultMessage: 'Kwota kaucji'
          })}
          value={
            depositValue
              ? formatMoney(Math.abs(parseFloat(depositValue)), currency)
              : '—'
          }
        />
      </Box>
    </CardShell>
  )

  const consentsCard = (
    <CardShell>
      <CardHeader icon={<GppGoodOutlined sx={{ fontSize: '16px' }} />}>
        {formatMessage({
          id: 'additionalServicesBookingAgreement.termsAcceptance',
          defaultMessage: 'Akceptacja warunków'
        })}
      </CardHeader>
      <Box sx={{ px: '24px', py: '16px' }}>
        <FormControlLabel
          sx={{ alignItems: 'flex-start', ml: 0, mr: 0, width: '100%' }}
          control={
            <Checkbox
              checked={checkboxTicked}
              disabled={!scrolledToEnd || !sessionId || acceptAgreement.isLoading}
              onChange={handleCheckboxChange}
              inputProps={{ 'aria-label': consentLabel }}
              sx={{
                p: '2px',
                mr: '10px',
                color: theme.r4r.greyLight,
                '&.Mui-checked': { color: theme.r4r.navy }
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: '12px', lineHeight: '18px', color: theme.r4r.ink, pt: '2px' }}>
              {consentLabel}
            </Typography>
          }
        />
        {attachments.length > 0 && (
          <Box sx={{ mt: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {attachments.map((attachment) => (
              <Link
                key={attachment.id}
                href={attachment.file}
                target='_blank'
                rel='noopener noreferrer'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: theme.r4r.azure,
                  textDecoration: 'none'
                }}>
                <AttachFileOutlined sx={{ fontSize: '16px' }} />
                {attachment.name}
              </Link>
            ))}
          </Box>
        )}
      </Box>
    </CardShell>
  )

  const signatureCard = (
    <CardShell>
      <CardHeader icon={<CreateOutlined sx={{ fontSize: '16px' }} />}>
        {formatMessage({
          id: 'additionalServicesBookingAgreement.electronicSignature',
          defaultMessage: 'Podpis elektroniczny'
        })}
      </CardHeader>
      <Box sx={{ px: '24px', pb: '16px', pt: '16px' }}>
        <ButtonBase
          onClick={() => setSignatureReady(true)}
          disabled={!sessionId || acceptAgreement.isLoading}
          sx={{
            width: '100%',
            height: '110px',
            backgroundColor: signatureReady ? alpha(theme.r4r.green, 0.08) : '#f9fafb',
            border: `2px dashed ${signatureReady ? theme.r4r.green : theme.r4r.greyLight}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
          {signatureReady ? (
            <CheckCircle sx={{ fontSize: '28px', color: theme.r4r.green }} />
          ) : (
            <CreateOutlined sx={{ fontSize: '28px', color: theme.r4r.grey }} />
          )}
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: signatureReady ? theme.r4r.green : theme.r4r.grey
            }}>
            {signatureReady
              ? formatMessage({
                  id: 'additionalServicesBookingAgreement.signatureDone',
                  defaultMessage: 'Podpis złożony'
                })
              : formatMessage({
                  id: 'additionalServicesBookingAgreement.signatureHint',
                  defaultMessage: 'Kliknij, aby złożyć podpis'
                })}
          </Typography>
        </ButtonBase>
      </Box>
    </CardShell>
  )

  const signButton = (
    <LoadingButton
      variant='contained'
      fullWidth
      disabled={acceptDisabled}
      loading={acceptAgreement.isLoading}
      onClick={handleAccept}
      startIcon={<CheckCircle sx={{ fontSize: '20px' }} />}
      sx={{
        backgroundColor: theme.r4r.green,
        color: '#fff',
        fontWeight: 700,
        fontSize: '14px',
        py: '14px',
        px: '24px',
        borderRadius: 0,
        boxShadow: 'none',
        ':hover': { backgroundColor: theme.r4r.green, boxShadow: 'none' },
        '&.Mui-disabled': {
          backgroundColor: alpha(theme.r4r.green, 0.4),
          color: '#fff'
        }
      }}>
      {acceptButtonLabel}
    </LoadingButton>
  )

  const content = (
    <>
      {header}
      {sessionError && (
        <Alert severity='warning' sx={{ mb: '16px' }}>
          {sessionError}
        </Alert>
      )}
      {isStartingSession && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: '16px', md: '32px' },
          alignItems: 'start'
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {hero}
          {documentCard}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {detailsCard}
          {consentsCard}
          {signatureCard}
          {signButton}
        </Box>
      </Box>
    </>
  )

  if (isDesktop) {
    return content
  }

  return (
    <>
      <BackNavigationHeader label={pageTitle} navigateTo={backTo} />
      <BackNavigationContent>
        <Box pt='env(safe-area-inset-top)' pb='24px'>
          {content}
        </Box>
      </BackNavigationContent>
    </>
  )
}

export default AdditionalServicesBookingAgreementConsent
